import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Select, Slider, Button, Space, Typography, Alert, Spin } from 'antd';
import { PlayCircleOutlined, PauseCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import { ENDPOINTS } from '../../helpers/api/apiConfig';
import { MetricLink } from '../MetricLink';
import MapGL, { Layer, Marker, Source } from 'react-map-gl';
import type { MapRef } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import dayjs, { type Dayjs } from 'dayjs';
import { trackActivity } from '../../helpers/analytics/trackActivity';
import { resolveColorRangesForMetric, resolveFieldRange } from '../../helpers/metricRules';

const { Text } = Typography;
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1IjoiZS1uYW5vIiwiYSI6ImNtamM0cjZzcjBtMW8zZnNiZ2I4NmljYTIifQ.MP4bn1hVGwhOXujMC4wjUw';
const toNumber = (value: unknown) => {
    if (value === null || value === undefined) return null;
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

interface PatternPointData {
    bucket: string;
    patternPointId: number;
    xLocal: number;
    yLocal: number;
    value: number;
}

interface AnimatedFrame {
    timestamp: string;
    displayDate: string;
    pointValues: Map<number, { value: number; x: number; y: number }>;
    avgValue: number | null;
}

interface PatternPoint {
    id: number;
    pointIndex: number;
    xLocal: number;
    yLocal: number;
    label?: string;
    gpsLat?: number;
    gpsLon?: number;
}

interface Pattern {
    id: number;
    name: string;
    numPoints: number;
    coordSystem?: string;
}

interface CommonSectionProps {
    section: any;
    data: any[];
    areas: any[];
    areaTypes: any[];
    metrics: any[];
    previewAreaId: number;
    dateRange?: [Dayjs, Dayjs];
    onMetricClick?: (metricKey: string, metricId: number | undefined, areaId: number) => void;
    onPatternPointClick?: (patternPointId: number, patternId: number, areaId: number) => void;
}

export const ReportAnimatedPatternPoints: React.FC<CommonSectionProps> = ({
    section,
    areas,
    areaTypes,
    metrics,
    previewAreaId,
    dateRange,
    onMetricClick,
    onPatternPointClick,
}) => {
    // React hooks MUST be called unconditionally
    const [patterns, setPatterns] = useState<Pattern[]>([]);
    const [selectedPatternId, setSelectedPatternId] = useState<number | null>(null);
    const [patternPoints, setPatternPoints] = useState<PatternPoint[]>([]);
    const [measurementData, setMeasurementData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingData, setLoadingData] = useState(false);
    const [currentFrameIndex, setCurrentFrameIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(true);
    const [playbackSpeed, setPlaybackSpeed] = useState(1000);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const [imageBounds, setImageBounds] = useState({ left: 0, top: 0, width: 0, height: 0 });
    const [templateImageFailed, setTemplateImageFailed] = useState(false);
    const [templateImageLoaded, setTemplateImageLoaded] = useState(false);
    const [areaGeometry, setAreaGeometry] = useState<any>(null);
    const previewRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);
    const mapRef = useRef<MapRef | null>(null);
    const normalizeDate = (value?: Dayjs | Date | string | null) => {
        if (!value) return undefined;
        const parsed = dayjs(value);
        return parsed.isValid() ? parsed : undefined;
    };

    // Find area and area type info
    const area = useMemo(() => areas.find((a: any) => a.id === previewAreaId), [areas, previewAreaId]);
    const areaType = useMemo(() => areaTypes.find((at: any) => at.id === area?.areaType?.id), [areaTypes, area]);
    const selectedPattern = useMemo(
        () => patterns.find((pattern) => pattern.id === selectedPatternId) || null,
        [patterns, selectedPatternId]
    );
    const metricByKey = useMemo(() => {
        const map = new Map<string, any>();
        metrics.forEach((metric: any) => {
            if (metric?.key) map.set(metric.key, metric);
        });
        return map;
    }, [metrics]);

    const templateImageUrl = useMemo(() => {
        if (areaType?.id && (areaType as any).templateImage) {
            return `${ENDPOINTS.AREA_TYPES_V2}/${areaType.id}/template-image?t=${Date.now()}`;
        }
        return undefined;
    }, [areaType]);

    useEffect(() => {
        setTemplateImageFailed(false);
        setTemplateImageLoaded(false);
    }, [templateImageUrl]);

    const hasLocalPoints = useMemo(
        () =>
            patternPoints.some((point) => {
                const x = toNumber(point.xLocal);
                const y = toNumber(point.yLocal);
                return x !== null && y !== null;
            }),
        [patternPoints]
    );
    const gpsPointLookup = useMemo<Map<number, { gpsLat: number; gpsLon: number }>>(() => {
        const map = new Map<number, { gpsLat: number; gpsLon: number }>();
        measurementData.forEach((row: any) => {
            const pointId = row?.patternPointId;
            if (pointId === null || pointId === undefined) return;
            const lat = toNumber(row.gpsLat);
            const lon = toNumber(row.gpsLon);
            if (lat === null || lon === null) return;
            if (!map.has(Number(pointId))) {
                map.set(Number(pointId), { gpsLat: lat, gpsLon: lon });
            }
        });
        return map;
    }, [measurementData]);
    const gpsPoints = useMemo<Array<{ id: number; pointIndex: number; label?: string; gpsLat: number; gpsLon: number }>>(() => {
        const resolved = patternPoints
            .map((point) => {
                const lat = toNumber(point.gpsLat);
                const lon = toNumber(point.gpsLon);
                const fallback = gpsPointLookup.get(point.id);
                const resolvedLat = lat ?? fallback?.gpsLat ?? null;
                const resolvedLon = lon ?? fallback?.gpsLon ?? null;
                if (resolvedLat === null || resolvedLon === null) return null;
                return {
                    id: point.id,
                    pointIndex: point.pointIndex,
                    label: point.label,
                    gpsLat: resolvedLat,
                    gpsLon: resolvedLon,
                };
            })
            .filter(Boolean) as Array<{ id: number; pointIndex: number; label?: string; gpsLat: number; gpsLon: number }>;

        if (resolved.length > 0) {
            return resolved;
        }

        return Array.from(gpsPointLookup.entries()).map(([id, coords]) => ({
            id,
            pointIndex: id,
            gpsLat: coords.gpsLat,
            gpsLon: coords.gpsLon,
        }));
    }, [patternPoints, gpsPointLookup]);
    const isGpsPattern = selectedPattern?.coordSystem
        ? selectedPattern.coordSystem.toLowerCase() !== 'local'
        : false;
    const hasTemplateImage = Boolean(templateImageUrl) && !templateImageFailed;
    const showTemplateView = hasTemplateImage && hasLocalPoints && !isGpsPattern;
    const showMapView = !showTemplateView;

    // Calculate image bounds for preview (same logic as Patterns.tsx)
    useEffect(() => {
        const calculateBoundsFromContainer = () => {
            const container = previewRef.current;
            if (!container) {
                return;
            }

            const containerWidth = container.clientWidth;
            const containerHeight = container.clientHeight;

            if (containerWidth === 0 || containerHeight === 0) {
                setTimeout(calculateBoundsFromContainer, 100);
                return;
            }

            if (!templateImageUrl) {
                // No template - use full container bounds
                setImageBounds({
                    left: 0,
                    top: 0,
                    width: containerWidth,
                    height: containerHeight,
                });
                return;
            }

            const image = imageRef.current;
            if (!image || !image.complete) {
                return;
            }

            const imageAspect = image.naturalWidth / image.naturalHeight;
            const containerAspect = containerWidth / containerHeight;

            let displayWidth, displayHeight, offsetX, offsetY;

            if (containerAspect > imageAspect) {
                displayHeight = containerHeight;
                displayWidth = displayHeight * imageAspect;
                offsetX = (containerWidth - displayWidth) / 2;
                offsetY = 0;
            } else {
                displayWidth = containerWidth;
                displayHeight = displayWidth / imageAspect;
                offsetX = 0;
                offsetY = (containerHeight - displayHeight) / 2;
            }

            setImageBounds({
                left: offsetX,
                top: offsetY,
                width: displayWidth,
                height: displayHeight,
            });
        };

        let isActive = true;

        if (hasTemplateImage && templateImageUrl) {
            const img = new Image();
            imageRef.current = img;

            img.onload = () => {
                if (!isActive) return;
                setTemplateImageLoaded(true);
                setTimeout(calculateBoundsFromContainer, 100);
            };

            img.onerror = (err) => {
                if (!isActive) return;
                console.error('[AnimatedPatternPoints] Image failed to load:', err);
                setTemplateImageFailed(true);
                setTemplateImageLoaded(false);
                setTimeout(calculateBoundsFromContainer, 100);
            };
            
            img.src = templateImageUrl;
        } else {
            // No template - set bounds immediately when container is ready
            setTemplateImageLoaded(true);
            setTimeout(calculateBoundsFromContainer, 100);
        }

        // Add resize observer to recalculate bounds on container resize
        const resizeObserver = new ResizeObserver(() => {
            calculateBoundsFromContainer();
        });

        if (previewRef.current) {
            resizeObserver.observe(previewRef.current);
        }

        return () => {
            isActive = false;
            resizeObserver.disconnect();
            imageRef.current = null;
        };
    }, [hasTemplateImage, selectedPatternId, templateImageUrl, loadingData, measurementData.length]);

    // Load area geometry for GPS-based patterns
    useEffect(() => {
        const loadAreaGeometry = async () => {
            if (!previewAreaId || !showMapView) {
                setAreaGeometry(null);
                return;
            }
            try {
                const res = await axios.get(ENDPOINTS.AREA_DETAIL(previewAreaId), { withCredentials: true });
                const payload = res.data?.data || res.data;
                setAreaGeometry(payload?.geom || null);
            } catch (err) {
                console.warn('Failed to load area geometry', err);
                setAreaGeometry(null);
            }
        };
        loadAreaGeometry();
    }, [previewAreaId, showMapView]);

    // Load available patterns for the selected area
    useEffect(() => {
        const loadPatterns = async () => {
            if (!previewAreaId) return;

            setLoading(true);
            try {
                const rangeFrom = normalizeDate(dateRange?.[0])?.startOf('day').toDate();
                const rangeTo = normalizeDate(dateRange?.[1])?.endOf('day').toDate();
                const fallbackFrom = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);

                const res = await axios.post(
                    ENDPOINTS.REPORT_SECTION_PATTERN_LIST,
                    {
                        areaIds: [previewAreaId],
                        dateRange: {
                            from: rangeFrom || fallbackFrom,
                            to: rangeTo || new Date(),
                        },
                    },
                    { withCredentials: true }
                );
                
                const patternData = res.data?.data || [];
                setPatterns(patternData);
                if (patternData.length === 0) {
                    setSelectedPatternId(null);
                    return;
                }

                const hasSelected = selectedPatternId
                    ? patternData.some((pattern: Pattern) => pattern.id === selectedPatternId)
                    : false;

                if (!hasSelected) {
                    setSelectedPatternId(patternData[0].id);
                }
            } catch (err) {
                console.error('Failed to load patterns:', err);
            } finally {
                setLoading(false);
            }
        };

        loadPatterns();
    }, [dateRange, previewAreaId]);

    // Load pattern points when pattern is selected
    useEffect(() => {
        const loadPatternPoints = async () => {
            if (!selectedPatternId) return;
            
            try {
                const res = await axios.get(
                    `${ENDPOINTS.PATTERNS}/${selectedPatternId}/points`,
                    { withCredentials: true }
                );
                
                const points = res.data?.data || res.data || [];
                console.log('[AnimatedPatternPoints] Loaded pattern points', {
                    patternId: selectedPatternId,
                    count: points.length,
                    sample: points.slice(0, 5).map((p: PatternPoint) => ({
                        id: p.id,
                        pointIndex: p.pointIndex,
                        xLocal: p.xLocal,
                        yLocal: p.yLocal,
                    })),
                });
                setPatternPoints(points);
            } catch (err) {
                console.error('Failed to load pattern points:', err);
            }
        };

        loadPatternPoints();
    }, [selectedPatternId]);

    useEffect(() => {
        if (!selectedPatternId) return;
        trackActivity({
            action: 'select',
            entityType: 'pattern',
            entityId: selectedPatternId,
            patternId: selectedPatternId,
            areaId: previewAreaId,
            reportTemplateId: (section as any)?.reportTemplateId,
            route: window.location.pathname,
        });
    }, [previewAreaId, section, selectedPatternId]);

    // Load measurement data when pattern is selected
    useEffect(() => {
        const loadMeasurementData = async () => {
            if (!selectedPatternId || !section.metrics || section.metrics.length === 0) {
                setMeasurementData([]);
                return;
            }
            
            setLoadingData(true);
            try {
                const resolution = section.resolution || 'day';
                const lookback = section.lookbackDays || 30;

                const rangeFrom = normalizeDate(dateRange?.[0])?.startOf('day').toDate();
                const rangeTo = normalizeDate(dateRange?.[1])?.endOf('day').toDate();

                const fromDate = rangeFrom || new Date(Date.now() - lookback * 24 * 60 * 60 * 1000);
                const toDate = rangeTo || new Date();
                console.log('[AnimatedPatternPoints] Request payload', {
                    areaId: previewAreaId,
                    patternId: selectedPatternId,
                    metrics: section.metrics,
                    resolution,
                    from: fromDate.toISOString(),
                    to: toDate.toISOString(),
                    dateRangeStart: rangeFrom ? rangeFrom.toISOString() : null,
                    dateRangeEnd: rangeTo ? rangeTo.toISOString() : null,
                });

                const res = await axios.post(
                    ENDPOINTS.REPORT_SECTION_ANIMATED_PATTERN_POINTS,
                    {
                        areaIds: [previewAreaId],
                        patternId: selectedPatternId,
                        metrics: section.metrics,
                        dateRange: {
                            from: fromDate,
                            to: toDate,
                        },
                        resolution,
                    },
                    { withCredentials: true }
                );
                
                const rows = res.data?.data || [];
                const missingBucket = rows.filter((row: any) => !row.bucket).length;
                const missingPointId = rows.filter(
                    (row: any) => row.patternPointId === null || row.patternPointId === undefined
                ).length;
                console.log('[AnimatedPatternPoints] Measurement rows', rows.length);
                console.log('[AnimatedPatternPoints] Measurement row gaps', {
                    missingBucket,
                    missingPointId,
                });
                console.log('[AnimatedPatternPoints] Measurement row sample', rows.slice(0, 5).map((row: any) => ({
                    bucket: row.bucket,
                    patternPointId: row.patternPointId,
                    xLocal: row.xLocal,
                    yLocal: row.yLocal,
                    average: row.average,
                })));
                setMeasurementData(rows);
            } catch (err) {
                console.error('Failed to load measurement data:', err);
                setMeasurementData([]);
            } finally {
                setLoadingData(false);
            }
        };

        loadMeasurementData();
    }, [
        selectedPatternId,
        previewAreaId,
        section.metrics,
        section.resolution,
        section.lookbackDays,
        dateRange,
    ]);

    // Pre-calculate all frames from measurement data
    const frames = useMemo((): AnimatedFrame[] => {
        if (!measurementData || measurementData.length === 0) return [];

        // Group by time bucket
        const bucketMap = new Map<string, PatternPointData[]>();
        
        measurementData.forEach((item: any) => {
            const bucket = item.bucket;
            if (!bucket) return;
            
            if (!bucketMap.has(bucket)) {
                bucketMap.set(bucket, []);
            }
            
            bucketMap.get(bucket)!.push({
                bucket,
                patternPointId: item.patternPointId,
                xLocal: item.xLocal,
                yLocal: item.yLocal,
                value: item.average,
            });
        });

        // Sort buckets by time
        const sortedBuckets = Array.from(bucketMap.entries()).sort((a, b) => {
            const dateA = new Date(a[0]);
            const dateB = new Date(b[0]);
            return dateA.getTime() - dateB.getTime();
        });

        // Create frames
        return sortedBuckets.map(([timestamp, points]) => {
            const pointValues = new Map<number, { value: number; x: number; y: number }>();
            let totalValue = 0;
            let valueCount = 0;

            points.forEach((p) => {
                if (p.patternPointId === null || p.patternPointId === undefined) return;
                pointValues.set(p.patternPointId, {
                    value: p.value,
                    x: p.xLocal,
                    y: p.yLocal,
                });
                totalValue += p.value;
                valueCount++;
            });

            const avgValue = valueCount > 0 ? totalValue / valueCount : null;
            
            const date = new Date(timestamp);
            const displayDate = isNaN(date.getTime()) 
                ? String(timestamp)
                : date.toLocaleDateString('en-GB', { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                });

            return {
                timestamp,
                displayDate,
                pointValues,
                avgValue,
            };
        });
    }, [measurementData]);

    useEffect(() => {
        if (!frames.length) return;
        const frame = frames[currentFrameIndex];
        if (!frame) return;
        const frameIds = new Set(Array.from(frame.pointValues.keys()));
        const missing = patternPoints
            .map((p) => p.id)
            .filter((id) => id != null && !frameIds.has(id));

        console.log('[AnimatedPatternPoints] Frame coverage', {
            patternId: selectedPatternId,
            bucket: frame.timestamp,
            framePoints: frameIds.size,
            patternPoints: patternPoints.length,
            missingSample: missing.slice(0, 5),
        });
    }, [frames, currentFrameIndex, patternPoints, selectedPatternId]);

    // Calculate global min/max for consistent color scaling (respects fixed mode)
    const globalValueRange = useMemo(() => {
        const metricKey = section.metrics?.[0];
        const metricConfig = metricKey ? metricByKey.get(metricKey) : null;
        const areaDefaultRange = resolveFieldRange(
            resolveColorRangesForMetric(metricConfig, area),
            'value'
        );

        if (
            section.valueRangeMode === 'areaDefault' &&
            areaDefaultRange &&
            areaDefaultRange.min !== null &&
            areaDefaultRange.min !== undefined &&
            areaDefaultRange.max !== null &&
            areaDefaultRange.max !== undefined
        ) {
            return {
                min: Number(areaDefaultRange.min),
                max: Number(areaDefaultRange.max),
            };
        }

        // Use fixed range if configured
        if (section.valueRangeMode === 'fixed' && 
            section.valueMin !== null && section.valueMin !== undefined &&
            section.valueMax !== null && section.valueMax !== undefined) {
            return { 
                min: Number(section.valueMin), 
                max: Number(section.valueMax) 
            };
        }

        // Auto mode: calculate from data
        if (frames.length === 0) {
            return { min: 0, max: 100 };
        }

        const allValues: number[] = [];
        frames.forEach(frame => {
            frame.pointValues.forEach(point => {
                allValues.push(point.value);
            });
        });

        if (allValues.length === 0) {
            return { min: 0, max: 100 };
        }

        const min = Math.min(...allValues);
        const max = Math.max(...allValues);
        
        // If min === max, add a small buffer to prevent division by zero in color calc
        if (min === max) {
            return { min: min - 1, max: min + 1 };
        }
        
        return { min, max };
    }, [frames, section.valueRangeMode, section.valueMin, section.valueMax, section.metrics, metricByKey, area]);

    // Color calculation function with all heatmap color schemes
    const getColorForValue = (value: number, min: number, max: number, scheme: string = 'blue-red', invert: boolean = false) => {
        let normalized = max > min ? (value - min) / (max - min) : 0.5;
        
        // Apply color inversion if enabled
        if (invert) {
            normalized = 1 - normalized;
        }
        
        if (scheme === 'blue-red') {
            const r = Math.round(255 * normalized);
            const b = Math.round(255 * (1 - normalized));
            return `rgb(${r}, 0, ${b})`;
        } else if (scheme === 'green-red') {
            const r = Math.round(255 * (1 - normalized));
            const g = Math.round(255 * normalized);
            return `rgb(${r}, ${g}, 0)`;
        } else if (scheme === 'red-yellow-green') {
            // Red (bad/low) → Yellow (mid) → Green (good/high)
            // 0.0-0.5: Red to Yellow
            // 0.5-1.0: Yellow to Green
            let r, g, b;
            if (normalized < 0.5) {
                // Red (255,0,0) → Yellow (255,255,0)
                const t = normalized * 2; // 0 to 1
                r = 255;
                g = Math.round(t * 255);
                b = 0;
            } else {
                // Yellow (255,255,0) → Green (0,255,0)
                const t = (normalized - 0.5) * 2; // 0 to 1
                r = Math.round((1 - t) * 255);
                g = 255;
                b = 0;
            }
            return `rgb(${r}, ${g}, ${b})`;
        } else if (scheme === 'yellow-red') {
            // Yellow (255, 255, 0) → Red (255, 0, 0)
            const g = Math.round(255 * (1 - normalized));
            return `rgb(255, ${g}, 0)`;
        } else if (scheme === 'viridis') {
            // Viridis color scale approximation
            // Purple → Blue → Green → Yellow
            if (normalized < 0.25) {
                const t = normalized / 0.25;
                const r = Math.round(68 + t * (59 - 68));
                const g = Math.round(1 + t * (82 - 1));
                const b = Math.round(84 + t * (139 - 84));
                return `rgb(${r}, ${g}, ${b})`;
            } else if (normalized < 0.5) {
                const t = (normalized - 0.25) / 0.25;
                const r = Math.round(59 + t * (33 - 59));
                const g = Math.round(82 + t * (145 - 82));
                const b = Math.round(139 + t * (140 - 139));
                return `rgb(${r}, ${g}, ${b})`;
            } else if (normalized < 0.75) {
                const t = (normalized - 0.5) / 0.25;
                const r = Math.round(33 + t * (94 - 33));
                const g = Math.round(145 + t * (201 - 145));
                const b = Math.round(140 + t * (98 - 140));
                return `rgb(${r}, ${g}, ${b})`;
            } else {
                const t = (normalized - 0.75) / 0.25;
                const r = Math.round(94 + t * (253 - 94));
                const g = Math.round(201 + t * (231 - 201));
                const b = Math.round(98 + t * (37 - 98));
                return `rgb(${r}, ${g}, ${b})`;
            }
        }
        
        // Default fallback
        return `rgb(${Math.round(255 * normalized)}, 0, ${Math.round(255 * (1 - normalized))})`;
    };

    // Auto-play effect
    useEffect(() => {
        if (!isPlaying || frames.length === 0) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        intervalRef.current = setInterval(() => {
            setCurrentFrameIndex((prev) => {
                return (prev + 1) % frames.length;
            });
        }, playbackSpeed);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [isPlaying, playbackSpeed, frames.length]);

    // Reset to first frame when data changes
    useEffect(() => {
        setCurrentFrameIndex(0);
    }, [measurementData]);

    const currentFrame = frames[currentFrameIndex];
    const sectionTitle = section.title || 'Animated points';
    const sectionMeta = useMemo(() => {
        const areaName = area?.name || 'No area';
        if (dateRange?.[0] && dateRange?.[1]) {
            return `${areaName} · ${dateRange[0].format('YYYY-MM-DD')} → ${dateRange[1].format('YYYY-MM-DD')}`;
        }
        return areaName;
    }, [area, dateRange]);

    const gpsBounds = useMemo(() => {
        if (gpsPoints.length === 0) return null;
        const lngs = gpsPoints.map((point) => point.gpsLon as number);
        const lats = gpsPoints.map((point) => point.gpsLat as number);
        return [
            [Math.min(...lngs), Math.min(...lats)],
            [Math.max(...lngs), Math.max(...lats)],
        ] as [[number, number], [number, number]];
    }, [gpsPoints]);

    const mapInitialViewState = useMemo(() => {
        if (areaGeometry?.coordinates?.[0]?.[0]) {
            return {
                longitude: areaGeometry.coordinates[0][0][0],
                latitude: areaGeometry.coordinates[0][0][1],
                zoom: 15,
            };
        }
        if (gpsPoints.length > 0) {
            return {
                longitude: gpsPoints[0].gpsLon as number,
                latitude: gpsPoints[0].gpsLat as number,
                zoom: 15,
            };
        }
        return { longitude: 0, latitude: 0, zoom: 2 };
    }, [areaGeometry, gpsPoints]);

    useEffect(() => {
        if (!showMapView || !mapRef.current) return;
        if (areaGeometry?.coordinates?.[0]) {
            const coords = areaGeometry.coordinates[0];
            const lngs = coords.map((coord: number[]) => coord[0]);
            const lats = coords.map((coord: number[]) => coord[1]);
            const bounds: [[number, number], [number, number]] = [
                [Math.min(...lngs), Math.min(...lats)],
                [Math.max(...lngs), Math.max(...lats)],
            ];
            mapRef.current.fitBounds(bounds, { padding: 40, duration: 800 });
            return;
        }
        if (gpsBounds) {
            mapRef.current.fitBounds(gpsBounds, { padding: 60, duration: 800 });
        }
    }, [areaGeometry, gpsBounds, showMapView]);

    // Create metric links
    const metricLinks = useMemo(() => {
        if (!section.metrics || section.metrics.length === 0 || !previewAreaId) {
            return null;
        }
        
        return (
            <Space size="small" className="report-pattern-metrics" wrap>
                {section.metrics.map((metricKey: string, metricIdx: number) => {
                    const metric = metrics.find(m => m.key === metricKey);
                    const metricName = metric?.name || metricKey;
                    
                    if (onMetricClick && previewAreaId) {
                        return (
                            <React.Fragment key={metricKey}>
                                {metricIdx > 0 && <Text type="secondary" style={{ fontSize: 12 }}> · </Text>}
                                <MetricLink
                                    metricKey={metricKey}
                                    metricName={metricName}
                                    metricId={metric?.id}
                                    areaId={previewAreaId}
                                    onClick={onMetricClick}
                                    style={{ fontSize: 12 }}
                                />
                            </React.Fragment>
                        );
                    }
                    return (
                        <Text key={metricKey} style={{ fontSize: 12 }}>
                            {metricIdx > 0 && ' · '}{metricName}
                        </Text>
                    );
                })}
            </Space>
        );
    }, [section.metrics, metrics, previewAreaId, onMetricClick]);

    const renderScale = () => (
        <div className="report-animated-pattern-scale">
            <div><strong>Scale:</strong></div>
            <div className="report-animated-pattern-scale-bar-row">
                <div
                    style={{
                        width: 100,
                        height: 10,
                        background: (section.colorScheme === 'red-yellow-green' || section.colorScheme === 'viridis')
                            ? `linear-gradient(to right, ${getColorForValue(globalValueRange.min, globalValueRange.min, globalValueRange.max, section.colorScheme || 'blue-red', section.invertColors || false)}, ${getColorForValue((globalValueRange.min + globalValueRange.max) / 2, globalValueRange.min, globalValueRange.max, section.colorScheme || 'blue-red', section.invertColors || false)}, ${getColorForValue(globalValueRange.max, globalValueRange.min, globalValueRange.max, section.colorScheme || 'blue-red', section.invertColors || false)})`
                            : `linear-gradient(to right, ${getColorForValue(globalValueRange.min, globalValueRange.min, globalValueRange.max, section.colorScheme || 'blue-red', section.invertColors || false)}, ${getColorForValue(globalValueRange.max, globalValueRange.min, globalValueRange.max, section.colorScheme || 'blue-red', section.invertColors || false)})`,
                        borderRadius: 2,
                    }}
                />
            </div>
            <div className="report-animated-pattern-scale-range">
                <span>{globalValueRange.min.toFixed(1)}</span>
                <span>{globalValueRange.max.toFixed(1)}</span>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="report-animated-pattern-points">
                <div className="report-animated-pattern-head">
                    <div className="report-animated-pattern-title">{sectionTitle}</div>
                    <div className="report-animated-pattern-meta">{sectionMeta}</div>
                </div>
                <div className="report-animated-pattern-state">
                    <Spin tip="Loading patterns..." />
                </div>
            </div>
        );
    }

    if (patterns.length === 0) {
        return (
            <div className="report-animated-pattern-points">
                <div className="report-animated-pattern-head">
                    <div className="report-animated-pattern-title">{sectionTitle}</div>
                    <div className="report-animated-pattern-meta">{sectionMeta}</div>
                </div>
                <Alert
                    message="No Patterns Found"
                    description="No patterns have been used for measurements in this area."
                    type="info"
                    showIcon
                />
            </div>
        );
    }

    return (
        <div className="report-animated-pattern-points">
            <div className="report-animated-pattern-head">
                <div className="report-animated-pattern-title">{sectionTitle}</div>
                <div className="report-animated-pattern-meta">{sectionMeta}</div>
            </div>

            <div className="report-animated-pattern-summary">
                <div className="report-animated-pattern-summary-main">
                    <div className="report-animated-pattern-selector-row">
                        <Text type="secondary">Pattern</Text>
                        <Select
                            style={{ width: 280 }}
                            className="report-pattern-select"
                            value={selectedPatternId}
                            onChange={setSelectedPatternId}
                            options={patterns.map(p => ({
                                label: `${p.name} (${p.numPoints} points)`,
                                value: p.id,
                            }))}
                        />
                    </div>
                    {metricLinks ? <div className="report-animated-pattern-links">{metricLinks}</div> : null}
                </div>
                <div className="report-animated-pattern-summary-side">
                    {selectedPattern ? (
                        <span className="report-animated-pattern-pill">
                            {selectedPattern.numPoints} points
                        </span>
                    ) : null}
                    {frames.length > 0 ? (
                        <span className="report-animated-pattern-frame-count">
                            {frames.length} frames
                        </span>
                    ) : null}
                </div>
            </div>

            {loadingData && (
                <div className="report-animated-pattern-state">
                    <Spin tip="Loading measurement data..." />
                </div>
            )}

            {!loadingData && frames.length === 0 && (
                <Alert
                    message="No Data"
                    description="No measurements found for the selected pattern in the date range."
                    type="warning"
                    showIcon
                />
            )}

            {!loadingData && frames.length > 0 && currentFrame && (
                <div className="report-animated-pattern-stage">
                    {showMapView ? (
                        gpsPoints.length === 0 ? (
                            <Alert
                                message="No GPS coordinates"
                                description="This pattern does not have GPS coordinates, so a map preview is not available."
                                type="info"
                                showIcon
                            />
                        ) : (
                            <div className="report-animated-pattern-stage-frame report-animated-pattern-stage-frame--map">
                                <MapGL
                                    ref={mapRef}
                                    mapboxAccessToken={MAPBOX_TOKEN}
                                    key={`animated-pattern-map-${previewAreaId}-${selectedPatternId}`}
                                    initialViewState={mapInitialViewState}
                                    style={{ width: '100%', height: '100%' }}
                                    mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
                                    onLoad={() => {
                                        if (areaGeometry?.coordinates?.[0] && mapRef.current) {
                                            const coords = areaGeometry.coordinates[0];
                                            const lngs = coords.map((coord: number[]) => coord[0]);
                                            const lats = coords.map((coord: number[]) => coord[1]);
                                            const bounds: [[number, number], [number, number]] = [
                                                [Math.min(...lngs), Math.min(...lats)],
                                                [Math.max(...lngs), Math.max(...lats)],
                                            ];
                                            mapRef.current.fitBounds(bounds, { padding: 40, duration: 800 });
                                        } else if (gpsBounds && mapRef.current) {
                                            mapRef.current.fitBounds(gpsBounds, { padding: 60, duration: 800 });
                                        }
                                    }}
                                >
                                    {areaGeometry && (
                                        <Source
                                            id="area"
                                            type="geojson"
                                            data={{
                                                type: 'Feature',
                                                geometry: areaGeometry,
                                                properties: {},
                                            }}
                                        >
                                            <Layer id="area-fill" type="fill" paint={{ 'fill-color': '#2f54eb', 'fill-opacity': 0.1 }} />
                                            <Layer id="area-outline" type="line" paint={{ 'line-color': '#1d39c4', 'line-width': 2 }} />
                                        </Source>
                                    )}
                                    {gpsPoints.map((point) => {
                                        const frameData = currentFrame.pointValues.get(point.id);
                                        const hasData = frameData !== undefined;
                                        const color = hasData
                                            ? getColorForValue(
                                                  frameData.value,
                                                  globalValueRange.min,
                                                  globalValueRange.max,
                                                  section.colorScheme || 'blue-red',
                                                  section.invertColors || false
                                              )
                                            : '#cccccc';
                                        const circleSize = section.circleSize || 20;
                                        const opacity = (section.pointOpacity ?? 100) / 100;
                                        return (
                                            <Marker
                                                key={`point-${point.id}`}
                                                longitude={point.gpsLon as number}
                                                latitude={point.gpsLat as number}
                                                anchor="center"
                                            >
                                                <div
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (onPatternPointClick && selectedPatternId) {
                                                            onPatternPointClick(point.id, selectedPatternId, previewAreaId);
                                                        }
                                                    }}
                                                    style={{
                                                        width: circleSize,
                                                        height: circleSize,
                                                        borderRadius: '50%',
                                                        backgroundColor: color,
                                                        opacity,
                                                        border: '2px solid white',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: section.labelFontSize || 10,
                                                        fontWeight: 'bold',
                                                        color: 'white',
                                                        cursor: onPatternPointClick ? 'pointer' : 'default',
                                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (onPatternPointClick) {
                                                            e.currentTarget.style.transform = 'scale(1.3)';
                                                            e.currentTarget.style.boxShadow = '0 0 12px rgba(250, 140, 22, 0.8)';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.transform = 'scale(1)';
                                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
                                                    }}
                                                    title={`Click to view reports for Point ${point.pointIndex}${point.label ? ` (${point.label})` : ''}${hasData ? `: ${frameData.value.toFixed(2)}` : ': No data'}`}
                                                >
                                                    {section.showLabels && (point.label || point.pointIndex)}
                                                </div>
                                            </Marker>
                                        );
                                    })}
                                </MapGL>
                                {/* Frame info overlay */}
                                <div className="report-animated-pattern-overlay">
                                    <div><strong>{currentFrame.displayDate}</strong></div>
                                    {currentFrame.avgValue !== null && (
                                        <div className="report-animated-pattern-overlay-meta">
                                            Avg: {currentFrame.avgValue.toFixed(2)}
                                        </div>
                                    )}
                                </div>

                                {renderScale()}
                            </div>
                        )
                    ) : (
                        <div
                            ref={previewRef}
                            className="report-animated-pattern-stage-frame"
                            style={{ backgroundColor: '#f0f0f0' }}
                        >
                            {/* Template background layer with opacity control */}
                            {hasTemplateImage && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        backgroundImage: `url(${templateImageUrl})`,
                                        backgroundSize: 'contain',
                                        backgroundRepeat: 'no-repeat',
                                        backgroundPosition: 'center',
                                        opacity: (section.templateOpacity ?? 50) / 100,
                                        zIndex: 1,
                                        pointerEvents: 'none',
                                    }}
                                />
                            )}
                            {/* Render pattern points as circles - only when bounds are calculated */}
                            {imageBounds.width > 0 && templateImageLoaded && patternPoints.map((point) => {
                                const xLocal = toNumber(point.xLocal);
                                const yLocal = toNumber(point.yLocal);
                                if (xLocal === null || yLocal === null) {
                                    return null;
                                }
                                const frameData = currentFrame.pointValues.get(point.id);
                                const hasData = frameData !== undefined;
                                const color = hasData
                                    ? getColorForValue(
                                          frameData.value,
                                          globalValueRange.min,
                                          globalValueRange.max,
                                          section.colorScheme || 'blue-red',
                                          section.invertColors || false
                                      )
                                    : '#cccccc';

                                const circleSize = section.circleSize || 20;
                                const opacity = (section.pointOpacity ?? 100) / 100;

                                return (
                                    <div
                                        key={point.id}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (onPatternPointClick && selectedPatternId) {
                                                onPatternPointClick(point.id, selectedPatternId, previewAreaId);
                                            }
                                        }}
                                        style={{
                                            position: 'absolute',
                                            left: `${imageBounds.left + xLocal * imageBounds.width}px`,
                                            top: `${imageBounds.top + (1 - yLocal) * imageBounds.height}px`,
                                            transform: 'translate(-50%, -50%)',
                                            width: circleSize,
                                            height: circleSize,
                                            borderRadius: '50%',
                                            backgroundColor: color,
                                            opacity,
                                            border: '2px solid white',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: section.labelFontSize || 10,
                                            fontWeight: 'bold',
                                            color: 'white',
                                            cursor: onPatternPointClick ? 'pointer' : 'default',
                                            transition: 'transform 0.2s, box-shadow 0.2s',
                                            zIndex: 2,
                                        }}
                                        onMouseEnter={(e) => {
                                            if (onPatternPointClick) {
                                                e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.3)';
                                                e.currentTarget.style.boxShadow = '0 0 12px rgba(250, 140, 22, 0.8)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)';
                                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
                                        }}
                                        title={`Click to view reports for Point ${point.pointIndex}${point.label ? ` (${point.label})` : ''}${hasData ? `: ${frameData.value.toFixed(2)}` : ': No data'}`}
                                    >
                                        {section.showLabels && (point.label || point.pointIndex)}
                                    </div>
                                );
                            })}

                            {/* Frame info overlay */}
                            <div className="report-animated-pattern-overlay" style={{ zIndex: 3 }}>
                                <div><strong>{currentFrame.displayDate}</strong></div>
                                {currentFrame.avgValue !== null && (
                                    <div className="report-animated-pattern-overlay-meta">
                                        Avg: {currentFrame.avgValue.toFixed(2)}
                                    </div>
                                )}
                            </div>

                            {renderScale()}
                        </div>
                    )}

                    <div className="report-animated-pattern-controls">
                        <div className="report-animated-pattern-controls-row">
                            <div className="report-animated-pattern-controls-left">
                                <Button
                                    icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                                    onClick={() => setIsPlaying(!isPlaying)}
                                    type={isPlaying ? 'primary' : 'default'}
                                >
                                    {isPlaying ? 'Pause' : 'Play'}
                                </Button>
                                <div className="report-animated-pattern-speed">
                                    <Text type="secondary">Speed</Text>
                                    <Select
                                        value={playbackSpeed}
                                        onChange={setPlaybackSpeed}
                                        style={{ width: 118 }}
                                        options={[
                                            { label: 'Slow', value: 2000 },
                                            { label: 'Normal', value: 1000 },
                                            { label: 'Fast', value: 500 },
                                            { label: 'Very Fast', value: 250 },
                                        ]}
                                    />
                                </div>
                            </div>
                            <div className="report-animated-pattern-controls-right">
                                <span className="report-animated-pattern-current-date">{currentFrame.displayDate}</span>
                                <span className="report-animated-pattern-current-status">{isPlaying ? 'Playing' : 'Paused'}</span>
                            </div>
                        </div>

                        <div className="report-animated-pattern-slider">
                            <Slider
                                min={0}
                                max={frames.length - 1}
                                value={currentFrameIndex}
                                onChange={(value) => {
                                    setCurrentFrameIndex(value);
                                    setIsPlaying(false);
                                }}
                                marks={frames.reduce((acc: Record<number, any>, frame, idx) => {
                                    const markInterval = Math.max(1, Math.floor(frames.length / 5));
                                    if (idx === 0 || idx === frames.length - 1 || idx % markInterval === 0) {
                                        const date = new Date(frame.timestamp);
                                        const shortDate = isNaN(date.getTime()) 
                                            ? frame.displayDate
                                            : date.toLocaleDateString('en-GB', { 
                                                day: '2-digit', 
                                                month: 'short' 
                                            });
                                        acc[idx] = {
                                            label: <span className="report-animated-pattern-mark">{shortDate}</span>,
                                        };
                                    }
                                    return acc;
                                }, {})}
                                tooltip={{
                                    formatter: (value) => value !== undefined ? (frames[value]?.displayDate || '') : '',
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReportAnimatedPatternPoints;
